import React, { useEffect, useState, useMemo } from 'react'
import 'reactflow/dist/base.css'
import '../components/charts/styles.css'
import ReactLoading from 'react-loading'
import logo from '../images/unsw.jpg'
import { PublicQuizAPI, ProgressResult, AttemptInfo, WrongOutcome, AreaProgress, CompetenceProgress } from '../apis/PublicQuizAPI'
import { stringSplitter } from '../utils/stringUtils'
import { formatReportDate } from '../utils/dateUtils'
import { Radar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

// Mirror of src/styles/report-colors.css — JS equivalents for use in Chart.js configs
const RC = {
  yellow:  '#ffdb00',
  blue:    '#4061c2',
  red:     '#fd635e',
  green:   '#19c986',
  pink:    '#fa93b8',
  sky:     '#2daae1',
  teal:    '#007881',
  purple:  '#8967c8',
  orange:  '#ff8200',
} as const

const scoreLevelName = (score: number) => {
  if (score >= 4) return 'Highly advanced'
  if (score >= 3) return 'Advanced'
  if (score >= 2) return 'Intermediate'
  if (score > 0) return 'Basic'
  return 'N/A'
}

const MiniAiGauge = ({ pct, id = 'ai' }: { pct: number | null; id?: string }) => {
  const trackX = 17, trackY = 22, trackW = 12, trackH = 100, rx = 6
  const fillH = pct !== null ? (pct / 100) * trackH : 0
  const fillY = trackY + trackH - fillH
  const ticks = [25, 50, 75]
  return (
    <div className="flex flex-col items-center" style={{ width: 46 }}>
      <svg viewBox="0 0 46 158" width="46" height="158">
        <defs>
          <clipPath id={`${id}-clip`}>
            <rect x={trackX} y={trackY} width={trackW} height={trackH} rx={rx} />
          </clipPath>
        </defs>
        {/* percentage label */}
        <text x="23" y="14" textAnchor="middle" style={{ fontSize: '11px', fontWeight: 'bold', fill: '#111', fontFamily: 'Arial' }}>
          {pct !== null ? `${Math.round(pct)}%` : '—'}
        </text>
        {/* green border */}
        <rect x={trackX - 1} y={trackY - 1} width={trackW + 2} height={trackH + 2} rx={rx + 1} fill="none" stroke="#1AC987" strokeWidth="1" />
        {/* grey track */}
        <rect x={trackX} y={trackY} width={trackW} height={trackH} rx={rx} fill="#f0f0f0" />
        {/* yellow fill from bottom */}
        <rect x={trackX} y={fillY} width={trackW} height={fillH} fill="#ffee99" clipPath={`url(#${id}-clip)`} />
        {/* tick marks inside the bar */}
        {ticks.map(t => {
          const ty = trackY + trackH - (t / 100) * trackH
          return <line key={t} x1={trackX + 2} y1={ty} x2={trackX + trackW - 2} y2={ty} stroke="#bbb" strokeWidth="1" />
        })}
        {/* bottom labels */}
        <text x="23" y={trackY + trackH + 16} textAnchor="middle" style={{ fontSize: '10px', fontWeight: 'bold', fill: '#222', fontFamily: 'Arial' }}>AI</text>
        <text x="23" y={trackY + trackH + 27} textAnchor="middle" style={{ fontSize: '9px', fill: '#666', fontFamily: 'Arial' }}>fluency</text>
      </svg>
    </div>
  )
}

/* Semicircle GaugeChart — commented out, replaced by donut ring below
const GaugeChart_semicircle = ({ label, score, maxScore = 4 }: { label: string; score: number; maxScore?: number; color?: string }) => {
  const cx = 100, cy = 100, r = 78, sw = 16
  const arcLen = Math.PI * r
  const fillLen = score > 0 ? Math.min((score / maxScore) * arcLen, arcLen) : 0
  return (
    <div className="flex flex-col items-center" style={{ width: 190 }}>
      <svg viewBox="0 0 200 114" width="190" height="109">
        <path d={`M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`} fill="none" stroke="#7de8c4" strokeWidth={sw + 2} strokeLinecap="round" />
        <path d={`M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`} fill="none" stroke="#f3f4f6" strokeWidth={sw} strokeLinecap="round" />
        <path d={`M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`} fill="none" stroke="#ffee99" strokeWidth={sw} strokeLinecap="round" strokeDasharray={`${fillLen} ${arcLen}`} />
        <text x={cx} y={cy - 22} textAnchor="middle" style={{ fontSize: '26px', fontWeight: 'bold', fill: '#000', fontFamily: 'Arial' }}>{score > 0 ? score.toFixed(1) : '—'}</text>
        <text x={cx} y={cy - 5} textAnchor="middle" style={{ fontSize: '11px', fill: '#555', fontFamily: 'Arial' }}>{scoreLevelName(score)}</text>
      </svg>
      <div style={{ marginTop: -4, fontSize: '13px', fontWeight: 'bold', textAlign: 'center', color: '#000', fontFamily: 'Arial' }}>{label}</div>
    </div>
  )
}
*/

const KSAConcentricChart = ({ kScore, sScore, aScore, maxScore = 4 }: { kScore: number; sScore: number; aScore: number; maxScore?: number }) => {
  const cx = 60, cy = 60, sw = 7
  const rings = [
    { label: 'Skill',     score: sScore, r: 50, color: RC.green },
    { label: 'Knowledge', score: kScore, r: 37, color: RC.blue },
    { label: 'Attitude',  score: aScore, r: 24, color: RC.orange },
  ]
  return (
    <div className="flex flex-col items-center" style={{ width: 220 }}>
      <svg viewBox="0 0 120 120" width="170" height="170">
        {rings.map(({ label, score, r, color }) => {
          const circ = 2 * Math.PI * r
          const fillLen = score > 0 ? Math.min((score / maxScore) * circ, circ) : 0
          return (
            <g key={label}>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ebebeb" strokeWidth={sw} />
              <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
                strokeDasharray={`${fillLen} ${circ}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${cx} ${cy})`} />
            </g>
          )
        })}
        {(() => {
          return (
            <text x={cx} y={cy + 4} textAnchor="middle"
              style={{ fontSize: '8px', fontWeight: 'bold', fill: '#000', fontFamily: 'Arial' }}>
              Digital
            </text>
          )
        })()}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
        {[
          { label: 'Attitude',  score: aScore, color: RC.orange },
          { label: 'Knowledge', score: kScore, color: RC.blue },
          { label: 'Skill',     score: sScore, color: RC.green },
        ].map(({ label, score, color }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="10" height="10" style={{ flexShrink: 0 }}><circle cx="5" cy="5" r="4.5" fill={color} /></svg>
            <span style={{ fontSize: '12px', fontFamily: 'Arial', color: '#333', width: 68, flexShrink: 0, textAlign: 'left' }}>
              {label}
            </span>
            <span style={{ fontSize: '10px', fontFamily: 'Arial', color: '#666', whiteSpace: 'nowrap', paddingLeft: 6 }}>
              {scoreLevelName(score)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const GaugeChart = ({ label, score, maxScore = 4, color = '#4061c2' }: { label: string; score: number; maxScore?: number; color?: string }) => {
  const cx = 50, cy = 50, r = 36, sw = 9
  const circ = 2 * Math.PI * r
  const fillLen = score > 0 ? Math.min((score / maxScore) * circ, circ) : 0
  return (
    <div className="flex flex-col items-center" style={{ width: 120 }}>
      <svg viewBox="0 0 100 100" width="110" height="110">
        {/* grey track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ebebeb" strokeWidth={sw} />
        {/* colored fill, starts from top */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeDasharray={`${fillLen} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} />
        {/* score */}
        <text x={cx} y={cy - 3} textAnchor="middle" style={{ fontSize: '20px', fontWeight: 'bold', fill: '#000', fontFamily: 'Arial' }}>
          {score > 0 ? score.toFixed(1) : '—'}
        </text>
        {/* level name */}
        <text x={cx} y={cy + 11} textAnchor="middle" style={{ fontSize: '9px', fill: '#555', fontFamily: 'Arial' }}>
          {scoreLevelName(score)}
        </text>
      </svg>
      <div style={{ marginTop: -4, fontSize: '13px', fontWeight: 'bold', textAlign: 'center', color: '#000', fontFamily: 'Arial' }}>
        {label}
      </div>
    </div>
  )
}

type AssessmentReportV3Props = {
  attemptId: string
}

const V3Footer = () => (
  <div className="v3-footer">
    <div className="font-roboto flex items-center gap-1 justify-end border-t border-gray-400 pt-2 pb-3 px-6 text-xs text-gray-500">
      <span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM8.89258 8.58887C10.5675 6.59294 13.3866 6.45808 15.21 8.2959C15.5988 8.68797 15.5961 9.32103 15.2041 9.70996C14.812 10.0988 14.179 10.0961 13.79 9.7041C12.7992 8.70562 11.3471 8.77503 10.4248 9.87402C9.43819 11.0497 9.43819 12.9503 10.4248 14.126C11.3471 15.225 12.7992 15.2944 13.79 14.2959C14.179 13.9039 14.812 13.9012 15.2041 14.29C15.5961 14.679 15.5988 15.312 15.21 15.7041C13.3866 17.5419 10.5675 17.4071 8.89258 15.4111C7.28244 13.492 7.28244 10.508 8.89258 8.58887Z" fill="#6b7280" />
        </svg>
      </span>
      2026 UNSW. All rights reserved.
    </div>
  </div>
)

// Compute the full narrative comment for a competence highlight in the Summary section.
// Used both when measuring section heights and when rendering.
function getCompetenceComment(c: CompetenceProgress): string {
  const s = c.score ?? 0
  const lvl = Math.max(1, Math.floor(s))
  const dominantType = c.types.length > 0
    ? c.types.reduce((best, t) => (t.score ?? 0) > (best.score ?? 0) ? t : best, c.types[0]).type
    : 'Knowledge'
  const name = c.competenceTitle || c.code
  if (dominantType === 'Attitude') {
    if (lvl <= 1) return `${name}: shows early-stage awareness of the attitudes and values relevant to this area.`
    if (lvl <= 2) return `${name}: demonstrates a developing orientation toward the attitudes and values in this area, applying them in familiar contexts.`
    if (lvl <= 3) return `${name}: consistently exhibits the attitudes and values expected at an advanced level in this area.`
    return `${name}: exemplifies highly advanced attitudes and values, reflecting deep professional commitment and judgment.`
  }
  if (dominantType === 'Skill') {
    if (lvl <= 1) return `${name}: demonstrates foundational skill, with early-stage practical ability in this area.`
    if (lvl <= 2) return `${name}: shows intermediate skill, able to perform tasks in this area across a range of typical situations.`
    if (lvl <= 3) return `${name}: performs at an advanced level, applying skills effectively in complex and varied situations.`
    return `${name}: exhibits highly advanced skill with exceptional practical capability and precision in this area.`
  }
  // Knowledge
  if (lvl <= 1) return `${name}: demonstrates basic awareness and foundational understanding in this area.`
  if (lvl <= 2) return `${name}: shows intermediate knowledge, with the ability to apply understanding across a range of situations.`
  if (lvl <= 3) return `${name}: demonstrates strong knowledge and understanding at an advanced level in this area.`
  return `${name}: exhibits highly advanced knowledge with exceptional depth and breadth of understanding.`
}

const LEVEL_MAP: Record<string, number> = {
  'Basic': 1,
  'Intermediate': 2,
  'Advanced': 3,
  'Highly advanced': 4,
}

/**
 * Pick up to `n` items from `candidates` with competence diversity as primary axis
 * and KSA type diversity as secondary:
 * - Round-robin across competences, picking 1 LO per comp per pass.
 * - When picking from a competence's group, prefer the KSA type least used so far.
 * - After one full pass, repeat with remaining LOs if still under n.
 */
function pickKSADiverse(candidates: WrongOutcome[], n: number): WrongOutcome[] {
  // Group by competence, preserving candidate order within each group
  const compBuckets = new Map<string, WrongOutcome[]>()
  for (const lo of candidates) {
    if (!compBuckets.has(lo.competenceCode)) compBuckets.set(lo.competenceCode, [])
    compBuckets.get(lo.competenceCode)!.push(lo)
  }
  const result: WrongOutcome[] = []
  const typeCounts: Record<string, number> = { Knowledge: 0, Skill: 0, Attitude: 0 }
  while (result.length < n) {
    let addedAny = false
    for (const [comp, bucket] of Array.from(compBuckets.entries())) {
      if (result.length >= n || bucket.length === 0) continue
      // Pick the LO from this comp whose type is least used so far
      bucket.sort((a: WrongOutcome, b: WrongOutcome) => (typeCounts[a.type] ?? 0) - (typeCounts[b.type] ?? 0))
      const item = bucket.shift()!
      result.push(item)
      typeCounts[item.type] = (typeCounts[item.type] ?? 0) + 1
      addedAny = true
      if (bucket.length === 0) compBuckets.delete(comp)
    }
    if (!addedAny) break
  }
  return result
}

export default function AssessmentReportV3({ attemptId }: AssessmentReportV3Props) {
  const [loading, setLoading] = useState(true)
  const [attemptInfo, setAttemptInfo] = useState<AttemptInfo | null>(null)
  const [progress, setProgress] = useState<ProgressResult | null>(null)
  const [wrongOutcomes, setWrongOutcomes] = useState<WrongOutcome[]>([])
  const [allOutcomes, setAllOutcomes] = useState<WrongOutcome[]>([])
  const [wrongOutcomePages, setWrongOutcomePages] = useState<WrongOutcome[][]>([])
  const [error, setError] = useState<string | null>(null)
  const measureRef = React.useRef<HTMLDivElement>(null)
  const isMeasured = React.useRef(false)

  type SectionKind = 'chart' | 'summary' | 'aod' | 'future'
  type CapSection = { areaIndex: number; kind: SectionKind }

  type AreaCalc = {
    area: AreaProgress
    areaIndex: number
    compNames: string[][]
    compLevels: number[]
    areaRadarData: any
    highScorers: CompetenceProgress[]
    summaryComments: string[]   // full comment per highScorer; empty when genericStrengthComment is used
    genericStrengthComment: string | null
    areaOutcomes: WrongOutcome[]
    futureGoals: WrongOutcome[]
    aiPct: number | null
    candidateAreaLevel: number
    minScore: number
  }

  const [capPageLayout, setCapPageLayout] = useState<CapSection[][] | null>(null)
  const capMeasureRef = React.useRef<HTMLDivElement>(null)
  const capMeasured = React.useRef(false)

  // Measure the page-1 header height so cap sections can fill the remaining space on page 1.
  // For multi-area reports the overview radar chart renders asynchronously, so the ResizeObserver
  // stays active (never disconnects early) and updates page1HeaderH whenever the height settles.
  const page1HeaderRef = React.useRef<HTMLDivElement>(null)
  const [page1HeaderH, setPage1HeaderH] = useState<number | null>(null)

  useEffect(() => {
    if (!progress) return
    const el = page1HeaderRef.current
    if (!el) return
    let lastH = 0
    const measure = () => {
      const h = page1HeaderRef.current?.getBoundingClientRect().height ?? 0
      if (h > 0 && h !== lastH) { lastH = h; setPage1HeaderH(h) }
    }
    // Try an immediate measurement (works for single-area; may be partial for multi-area)
    measure()
    // ResizeObserver captures any subsequent chart-driven height changes
    const obs = new ResizeObserver(measure)
    obs.observe(el)
    return () => obs.disconnect()
  }, [progress])

  // Flatten wrongOutcomes into area > type grouped render items
  type RenderItem =
    | { kind: 'area'; areaCode: string; areaTitle: string }
    | { kind: 'type'; type: string }
    | { kind: 'lo'; lo: WrongOutcome }

  const renderItems = React.useMemo<RenderItem[]>(() => {
    const items: RenderItem[] = []
    let lastArea = '', lastType = ''
    for (const lo of wrongOutcomes) {
      if (lo.areaCode !== lastArea) {
        items.push({ kind: 'area', areaCode: lo.areaCode, areaTitle: lo.areaTitle || lo.areaCode })
        lastArea = lo.areaCode
        lastType = ''
      }
      if (lo.type !== lastType) {
        items.push({ kind: 'type', type: lo.type })
        lastType = lo.type
      }
      items.push({ kind: 'lo', lo })
    }
    return items
  }, [wrongOutcomes])

  // Pre-compute per-area capability section data for dynamic layout
  const areaCalcList = useMemo<AreaCalc[]>(() => {
    if (!progress) return []
    const intLevel = (s: number) => Math.max(1, Math.ceil(s))

    return progress.areas.map((area, areaIndex) => {
      const compNames = area.competences.map(c => stringSplitter(c.competenceTitle || c.code, 20))
      const compLevels = area.competences.map(c =>
        c.score ?? (c.estimatedLevel ? LEVEL_MAP[c.estimatedLevel] : 0)
      )
      const areaRadarData = {
        labels: compNames,
        datasets: [{ label: 'Your proficiency level', data: compLevels, backgroundColor: '#ffdc004d', borderColor: '#FFDC00', pointBackgroundColor: '#007882', pointBorderColor: '#007882', borderWidth: 1 }],
      }
      const compScores = area.competences.map(c => c.score ?? 0)
      const areaMean = compScores.reduce((s, v) => s + v, 0) / (compScores.length || 1)
      const maxScore = Math.max(...compScores)
      const minScore = compScores.length > 0 ? Math.min(...compScores) : 0
      const scoresSpread = maxScore - minScore
      const compIntLevels = compScores.map(intLevel)
      const minIntLevel = compIntLevels.length > 0 ? Math.min(...compIntLevels) : 1
      const allSameLevel = compIntLevels.every(l => l === minIntLevel)
      const showIndividuals = scoresSpread >= 0.65 || !allSameLevel
      const highScorers = showIndividuals
        ? area.competences.filter(c => (c.score ?? 0) >= minScore + 0.65 || intLevel(c.score ?? 0) > minIntLevel)
        : []
      const meanIntLevel = Math.max(1, Math.round(areaMean))
      const genericStrengthComment = !showIndividuals
        ? meanIntLevel <= 1
          ? `All competences in this area are performed at a consistent basic level, demonstrating foundational understanding throughout.`
          : meanIntLevel <= 2
          ? `All competences in this area are at a consistent intermediate level, with balanced applied knowledge across the board.`
          : meanIntLevel <= 3
          ? `All competences in this area are at a consistent advanced level, reflecting strong and even expertise throughout.`
          : `All competences in this area are at a consistent highly advanced level, showing exceptional and uniform mastery across the board.`
        : null
      const candidateAreaLevel = Math.max(1, Math.floor(areaMean))
      const compNextLevelByCode = new Map(
        area.competences.map(c => [c.code, Math.max(1, Math.floor(c.score ?? 0)) + 1])
      )
      const areaOutcomes = pickKSADiverse(
        wrongOutcomes
          .filter(lo => lo.areaCode === area.areaCode)
          .sort((a, b) => (LEVEL_MAP[a.proficiencyLevel] ?? 99) - (LEVEL_MAP[b.proficiencyLevel] ?? 99)),
        3
      )
      const areaOutcomeCodes = new Set(areaOutcomes.map(lo => lo.code))
      const futureCandidates = allOutcomes
        .filter(lo => {
          if (lo.areaCode !== area.areaCode) return false
          const compNext = compNextLevelByCode.get(lo.competenceCode)
          return compNext !== undefined && LEVEL_MAP[lo.proficiencyLevel] === compNext && compNext <= 4
        })
      const futureGoals = pickKSADiverse(
        [
          ...futureCandidates.filter(lo => !areaOutcomeCodes.has(lo.code)),
          ...futureCandidates.filter(lo => areaOutcomeCodes.has(lo.code)),
        ],
        3
      )
      const aiPct = (area.aiTotal ?? 0) > 0 ? Math.round((area.aiCorrect ?? 0) / (area.aiTotal ?? 1) * 100) : null

      const summaryComments = highScorers.map(getCompetenceComment)

      return { area, areaIndex, compNames, compLevels, areaRadarData, highScorers, summaryComments, genericStrengthComment, areaOutcomes, futureGoals, aiPct, candidateAreaLevel, minScore }
    })
  }, [progress, wrongOutcomes, allOutcomes])

  // Flat ordered list of sections to render for capability details
  const capSections = useMemo<CapSection[]>(() => {
    return areaCalcList.flatMap(d => {
      const sections: CapSection[] = [{ areaIndex: d.areaIndex, kind: 'chart' }]
      if (d.highScorers.length > 0 || d.genericStrengthComment) sections.push({ areaIndex: d.areaIndex, kind: 'summary' })
      if (d.areaOutcomes.length > 0) sections.push({ areaIndex: d.areaIndex, kind: 'aod' })
      if (d.futureGoals.length > 0) sections.push({ areaIndex: d.areaIndex, kind: 'future' })
      return sections
    })
  }, [areaCalcList])

  // Measure rendered capability section heights, then compute page layout.
  // We debounce on page1HeaderH so that the layout is only computed once the header height
  // has stabilised (the overview radar chart renders asynchronously which causes several
  // ResizeObserver callbacks before settling on the final height).
  const page1HeaderHRef = React.useRef<number | null>(null)
  useEffect(() => {
    if (capSections.length === 0) { setCapPageLayout([]); capMeasured.current = false; return }
    if (page1HeaderH === null) return
    capMeasured.current = false

    // Pages 2+: 1122px height - 32px top padding - 32px bottom padding - 41px footer ≈ 1017px.
    // Use 990 for a safety margin.
    const USABLE_H = 990
    // Page 1 cap sections have NO extra vertical padding (header div already owns the 32px top).
    // Budget = page height - header height - footer height - small safety margin.
    // 1122 - headerH - 45 (footer+gap) - 10 (safety)
    const PAGE_H = 1122
    const FOOTER_H = 55 // footer height + breathing room
    const firstPageBudget = page1HeaderH > 0 ? Math.max(0, PAGE_H - page1HeaderH - FOOTER_H) : USABLE_H

    const runLayout = (budget: number) => {
      const container = capMeasureRef.current
      if (!container || container.children.length !== capSections.length) return false
      const children = Array.from(container.children) as HTMLElement[]
      if (children.some(el => el.getBoundingClientRect().height === 0)) return false

      const pages: CapSection[][] = []
      let currentPage: CapSection[] = []
      let usedH = 0
      let pageIndex = 0

      children.forEach((el, i) => {
        const h = el.getBoundingClientRect().height
        const pageBudget = pageIndex === 0 ? budget : USABLE_H
        // For page 0: enforce strictly — if a section doesn't fit (even the first one),
        // overflow immediately to prevent content overlapping the footer on page 1.
        // For pages 1+: only overflow when the page already has content, so an oversized
        // section that can never fit doesn't cause an infinite loop.
        const shouldOverflow = pageIndex === 0
          ? usedH + h > pageBudget
          : usedH + h > pageBudget && currentPage.length > 0
        if (shouldOverflow) {
          pages.push(currentPage)
          currentPage = []
          usedH = 0
          pageIndex++
        }
        currentPage.push(capSections[i])
        usedH += h
      })
      if (currentPage.length > 0) pages.push(currentPage)

      capMeasured.current = true
      setCapPageLayout(pages)
      return true
    }

    // Debounce: wait 120ms after the last page1HeaderH change before computing layout.
    // This prevents intermediate radar-chart resize events from producing a premature layout.
    page1HeaderHRef.current = page1HeaderH
    const timer = setTimeout(() => {
      // Only run if page1HeaderH hasn't changed again since this timer was scheduled.
      if (page1HeaderHRef.current !== page1HeaderH) return
      if (runLayout(firstPageBudget)) return
      // Measurement container not ready yet — observe and retry.
      const observer = new ResizeObserver(() => {
        if (page1HeaderHRef.current !== page1HeaderH) { observer.disconnect(); return }
        if (runLayout(firstPageBudget)) observer.disconnect()
      })
      if (capMeasureRef.current) observer.observe(capMeasureRef.current)
    }, 120)

    return () => clearTimeout(timer)
  }, [capSections, page1HeaderH])

  // Two-pass: measure only LO item heights, then compute page breaks with fixed header constants
  useEffect(() => {
    if (wrongOutcomes.length === 0) { setWrongOutcomePages([]); isMeasured.current = false; return }
    isMeasured.current = false

    const measure = () => {
      const container = measureRef.current
      if (!container || container.children.length !== wrongOutcomes.length) return false
      const children = Array.from(container.children) as HTMLElement[]
      if (children.every(el => el.getBoundingClientRect().height === 0)) return false

      const PAGE_HEIGHT = 1122 - 64        // usable height (top+bottom padding = 64)
      const FIRST_PAGE_RESERVE = 130       // heading + intro text on page 1
      const AREA_HEADER_H = 44             // pt-6 + text + border (~24px padding + 20px text)
      const TYPE_HEADER_H = 28             // pt-2 + text (~8px padding + 20px text)
      const LO_GAP = 4                     // py-0.5 * 2

      const pages: WrongOutcome[][] = []
      let currentPage: WrongOutcome[] = []
      let usedHeight = FIRST_PAGE_RESERVE
      let pageLastArea = ''
      let pageLastType = ''

      wrongOutcomes.forEach((lo, i) => {
        const loH = children[i].getBoundingClientRect().height + LO_GAP
        // Calculate overhead for headers that would appear before this item on the current page
        const needsAreaHeader = lo.areaCode !== pageLastArea
        const needsTypeHeader = lo.type !== pageLastType || needsAreaHeader
        const overhead = (needsAreaHeader ? AREA_HEADER_H : 0) + (needsTypeHeader ? TYPE_HEADER_H : 0)

        if (usedHeight + overhead + loH > PAGE_HEIGHT && currentPage.length > 0) {
          pages.push(currentPage)
          currentPage = []
          usedHeight = 0
          pageLastArea = ''
          pageLastType = ''
        }

        // Re-check headers for the (possibly new) page
        const newNeedsAreaHeader = lo.areaCode !== pageLastArea
        const newNeedsTypeHeader = lo.type !== pageLastType || newNeedsAreaHeader
        if (newNeedsAreaHeader) { usedHeight += AREA_HEADER_H; pageLastArea = lo.areaCode; pageLastType = '' }
        if (newNeedsTypeHeader) { usedHeight += TYPE_HEADER_H; pageLastType = lo.type }

        currentPage.push(lo)
        usedHeight += loH
      })
      if (currentPage.length > 0) pages.push(currentPage)
      isMeasured.current = true
      setWrongOutcomePages(pages)
      return true
    }

    if (measure()) return
    const observer = new ResizeObserver(() => { if (measure()) observer.disconnect() })
    if (measureRef.current) observer.observe(measureRef.current)
    return () => observer.disconnect()
  }, [wrongOutcomes])

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        const [info, prog, { outcomes, allOutcomes: all }] = await Promise.all([
          PublicQuizAPI.getAttemptInfo(attemptId),
          PublicQuizAPI.getProgress(attemptId),
          PublicQuizAPI.getWrongOutcomes(attemptId),
        ])
        setAttemptInfo(info)
        setProgress(prog)
        // If arrived via legacy ?attemptId=<ObjectId>, redirect URL to use resultCode
        if (info.resultCode && !new URLSearchParams(window.location.search).has('resultCode')) {
          const params = new URLSearchParams(window.location.search)
          params.delete('attemptId')
          params.set('resultCode', info.resultCode)
          window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
        }
        const TYPE_ORDER: Record<string, number> = { Attitude: 0, Knowledge: 1, Skill: 2 }
        const loSort = (a: WrongOutcome, b: WrongOutcome) =>
          a.areaCode.localeCompare(b.areaCode, undefined, { numeric: true }) ||
          (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9) ||
          a.competenceCode.localeCompare(b.competenceCode, undefined, { numeric: true })
        setWrongOutcomes([...outcomes].sort(loSort))
        setAllOutcomes([...all].sort(loSort))
      } catch (err: any) {
        console.error("Failed to load V3 report data", err)
        setError("Could not load report data.")
      } finally {
        setLoading(false)
      }
    }
    if (attemptId) {
      loadData()
    }
  }, [attemptId])

  if (loading) {
    return (
      <div className="report-v3-wrapper">
        <div className="report-page flex items-center justify-center" style={{ minHeight: '1122px' }}>
          <ReactLoading
            type={'spinningBubbles'}
            color={'#888888'}
            height={'64px'}
            width={'64px'}
          />
        </div>
      </div>
    )
  }

  if (error || !attemptInfo || !progress) {
    return (
      <div className="report-v3-wrapper">
        <div className="report-page p-10 text-black" style={{ minHeight: '1122px' }}>
          <h2 className="text-xl text-red-500">Error Loading Report</h2>
          <p>{error || "Insufficient data."}</p>
        </div>
      </div>
    )
  }

  // Overall + KSA scores — use server-computed values (geometric mean of posteriorScoreMap)
  const meanScore = progress.overallScore ?? 0

  let overallLevelName = "Beginner"
  if (meanScore >= 4) overallLevelName = "Highly advanced"
  else if (meanScore >= 3) overallLevelName = "Advanced"
  else if (meanScore >= 2) overallLevelName = "Intermediate"
  else if (meanScore > 0) overallLevelName = "Basic"

  const knowledgeScore = progress.kScore ?? 0
  const skillScore = progress.sScore ?? 0
  const attitudeScore = progress.aScore ?? 0

  // Radar Chart Prep
  const focusAreaNames = progress.areas.map(a => stringSplitter(a.areaTitle || a.areaCode, 20))
  const focusAreaLevels = progress.areas.map(a => a.score ?? (a.estimatedLevel ? LEVEL_MAP[a.estimatedLevel] : 0))

  const displayGrades = [
    { difficulty: 'Basic', from: 1, to: 1 },
    { difficulty: 'Intermediate', from: 2, to: 2 },
    { difficulty: 'Advanced', from: 3, to: 3 },
    { difficulty: 'Highly advanced', from: 4, to: 4 },
  ]

  const topLevel = 5

  const overallAiTotal = progress.areas.reduce((sum, a) => sum + (a.aiTotal ?? 0), 0)
  const overallAiCorrect = progress.areas.reduce((sum, a) => sum + (a.aiCorrect ?? 0), 0)
  const overallAiPct = overallAiTotal > 0 ? Math.round(overallAiCorrect / overallAiTotal * 100) : null
  const coursesParams = new URLSearchParams(window.location.search)
  coursesParams.delete('attemptId')
  coursesParams.delete('page')
  coursesParams.set('v', '3')
  coursesParams.set('resultCode', attemptInfo.resultCode ?? attemptId)
  const coursesHref = `/courses?${coursesParams.toString()}`

  // KSA scores per area for the overview radar
  const focusAreaKScores = progress.areas.map(a => {
    const ks = a.competences.flatMap(c => c.types.filter(t => t.type === 'Knowledge' && t.score !== null).map(t => t.score as number))
    return ks.length > 0 ? +(Math.pow(ks.reduce((acc, v) => acc * Math.max(0.1, v), 1), 1 / ks.length)).toFixed(2) : 0
  })
  const focusAreaSScores = progress.areas.map(a => {
    const ss = a.competences.flatMap(c => c.types.filter(t => t.type === 'Skill' && t.score !== null).map(t => t.score as number))
    return ss.length > 0 ? +(Math.pow(ss.reduce((acc, v) => acc * Math.max(0.1, v), 1), 1 / ss.length)).toFixed(2) : 0
  })
  const focusAreaAScores = progress.areas.map(a => {
    const as_ = a.competences.flatMap(c => c.types.filter(t => t.type === 'Attitude' && t.score !== null).map(t => t.score as number))
    return as_.length > 0 ? +(Math.pow(as_.reduce((acc, v) => acc * Math.max(0.1, v), 1), 1 / as_.length)).toFixed(2) : 0
  })

  /* KSA split (dots only, no lines) — commented out, revert by swapping datasets below
  const focusAreasRadarData_ksa = {
    labels: focusAreaNames,
    datasets: [
      {
        label: 'Knowledge',
        data: focusAreaKScores,
        fill: false,
        borderColor: RC.blue,
        pointBackgroundColor: RC.blue,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 5,
        borderWidth: 0,
      },
      {
        label: 'Skill',
        data: focusAreaSScores,
        fill: false,
        borderColor: RC.green,
        pointBackgroundColor: RC.green,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 5,
        borderWidth: 0,
      },
      {
        label: 'Attitude',
        data: focusAreaAScores,
        fill: false,
        borderColor: RC.orange,
        pointBackgroundColor: RC.orange,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 5,
        borderWidth: 0,
      },
    ],
  }
  */

  const focusAreasRadarData = {
    labels: focusAreaNames,
    datasets: [
      {
        label: 'Overall',
        data: focusAreaLevels,
        backgroundColor: '#ffdc004d',
        borderColor: '#FFDC00',
        pointBackgroundColor: '#FFDC00',
        pointBorderColor: '#FFDC00',
        borderWidth: 1,
        pointRadius: 0,
        pointStyle: 'circle' as const,
      },
      {
        label: 'Attitude',
        data: focusAreaAScores,
        fill: false,
        borderColor: RC.orange,
        pointBackgroundColor: RC.orange + 'aa',
        pointBorderColor: RC.orange,
        pointBorderWidth: 0,
        pointRadius: 3,
        borderWidth: 0,
        pointStyle: 'circle' as const,
      },
      {
        label: 'Knowledge',
        data: focusAreaKScores,
        fill: false,
        borderColor: RC.blue,
        pointBackgroundColor: RC.blue + 'aa',
        pointBorderColor: RC.blue,
        pointBorderWidth: 0,
        pointRadius: 3,
        borderWidth: 0,
        pointStyle: 'circle' as const,
      },
      {
        label: 'Skill',
        data: focusAreaSScores,
        fill: false,
        borderColor: RC.green,
        pointBackgroundColor: RC.green + 'aa',
        pointBorderColor: RC.green,
        pointBorderWidth: 0,
        pointRadius: 3,
        borderWidth: 0,
        pointStyle: 'circle' as const,
      },
    ],
  }

  const overviewRadarOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 380 / 300,
    devicePixelRatio: 8,
    layout: { padding: { top: 0, bottom: 36, left: 16, right: 16 } },
    scales: {
      r: {
        beginAtZero: true,
        suggestedMax: topLevel,
        grid: { color: '#1AC987' },
        angleLines: { color: '#1AC987' },
        ticks: {
          stepSize: Math.round(topLevel / 5),
          fontSize: 40,
          color: '#000000',
          font: { size: 14, family: 'Arial' },
          backdropColor: 'transparent',
          callback: (v: any) => v === topLevel ? '' : v,
        },
        pointLabels: {
          font: { size: 14, weight: 'bold' as const, family: 'Arial' },
          color: '#000000',
        },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          font: { size: 12, family: 'Arial' },
          color: '#000000',
          usePointStyle: true,
          pointStyleWidth: 16,
          padding: 14,
        },
      },
    },
  }

  const areaRadarOpts = {
    responsive: true, maintainAspectRatio: true, aspectRatio: 500 / 420,
    devicePixelRatio: 8,
    layout: { padding: { top: 0, bottom: 0, left: 16, right: 16 } },
    scales: {
      r: {
        beginAtZero: true,
        suggestedMax: topLevel,
        grid: { color: '#1AC987' },
        angleLines: { color: '#1AC987' },
        ticks: {
          stepSize: Math.round(topLevel / 5),
          fontSize: 40,
          color: '#000000',
          font: { size: 12, family: 'Arial' },
          backdropColor: 'transparent',
          callback: (v: any) => v === topLevel ? '' : v,
        },
        pointLabels: { font: { size: 12, family: 'Arial' }, color: '#000000' },
      },
    },
    plugins: { legend: { display: false } },
  }

  return (
    <div className="report-v3-wrapper" data-ready="true">
      {/* PAGE 1: Overview (+ capability sections when single-area) */}
      <div className="report-page flex flex-col" style={{ minHeight: '1122px' }}>
        <div ref={page1HeaderRef} style={{ padding: `32px 40px ${progress.areas.length > 1 ? 24 : 12}px` }}>
        {/* Header & Personal Details */}
        <div className="flex justify-between items-center gap-6 text-black">
          <div className="self-start">
            <img src={logo} alt="logo" className="w-[300px]" />
            <div className="text-2xl font-bold pt-7 text-black">
              {attemptInfo.assessmentReportName || attemptInfo.assessmentName || 'V3 Assessment'}
            </div>
            <div className={`pt-3 font-roboto text-sm text-black ${progress.areas.length > 1 ? 'pb-6' : 'pb-0'}`}>
              <div>
                {attemptInfo.assessmentDescription?.split(/\n|\\n/).map((line, i, arr) => {
                  if (line.startsWith('- ') || line.startsWith('-')) {
                    const text = line.startsWith('- ') ? line.slice(2) : line.slice(1)
                    return (
                      <ul key={i} className="list-disc list-outside ml-4">
                        <li>{text}</li>
                      </ul>
                    )
                  }
                  return <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                })}{' '}
                {progress.areas.length > 1 && (
                  <>It assesses your proficiency in each of the{' '}
                  <b>{progress.areas.length}</b> following areas:
                  <ul className="list-disc list-outside mt-1 ml-4">
                    {progress.areas.map((a, i) => (
                      <li key={i}>{a.areaTitle || a.areaCode}</li>
                    ))}
                  </ul>
                  </>
                )}
                <p className="mt-2 mb-4">
                  This graphic shows how you performed across questions assessing: <br />
                  <b>knowledge</b> - concepts you demonstrated you know<br/>
                  <b>skills</b> - techniques you demonstrated being able to apply<br/>
                  <b>attitude</b> - your approach and mindset to digital working
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 items-start">
            <div className="text-[32px] font-bold text-black border-none">
              {attemptInfo.candidate?.name || 'Simulated Candidate'}
            </div>
            <div className="text-sm font-semibold font-roboto text-black">
              Test date: {formatReportDate(attemptInfo.completedAt ?? attemptInfo.createdAt)}
            </div>
            <div className="text-sm font-semibold font-roboto text-black">
              Result code: {attemptInfo.resultCode ?? attemptId}
            </div>

            {/* Score Box */}
            <div className="flex flex-col text-center overflow-hidden border border-1 border-primaryUnsw mt-5 w-[300px]">
              <div className="font-bold flex items-center justify-center text-tertiaryTeal text-[26px] pt-3 pb-1">
                Overall level
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', textAlign: 'center', paddingBottom: 2, color: '#000', whiteSpace: 'nowrap' }}>
                {meanScore > 0 ? overallLevelName : 'Insufficient Data'}
              </div>
              <div className="flex justify-center pb-4">
                <KSAConcentricChart kScore={knowledgeScore} sScore={skillScore} aScore={attitudeScore} />
              </div>
            </div>
          </div>
        </div>

        {/* Radar Charts Overview */}
        {progress.areas.length > 1 && (
        <div className="pt-2 text-left">
          <div className="text-2xl font-bold text-black">Overview</div>
          <div className="mx-[-32px]">
            <div className="flex items-end justify-center">
              <div style={{ width: '90%', transform: 'scale(0.98)', transformOrigin: 'top right', marginTop: -16 }}>
                <Radar data={focusAreasRadarData} options={overviewRadarOptions} />
              </div>
              <div className="flex-shrink-0 flex items-center pr-4" style={{ marginBottom: 90, marginLeft: -100 }}>
                <MiniAiGauge pct={overallAiPct} id="ai-overview" />
              </div>
            </div>
            {/* grade map */}
            <div className="flex flex-row flex-wrap gap-x-8 gap-y-1 justify-center px-4" style={{ marginTop: -50, marginBottom: -8 }}>
              {displayGrades.map((displayGrade, index) => (
                <div className="text-sm font-bold text-tertiaryTeal" key={`displayGrade-${index}`}>
                  <span>
                    {displayGrade.from === displayGrade.to
                      ? `Level ${displayGrade.from}: `
                      : `Level ${displayGrade.from} to ${displayGrade.to}: `}
                  </span>
                  <span className="text-black">{displayGrade.difficulty}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
        )}
        </div>

        {/* Render capability sections that fit on page 1 directly after the header (both single and multi-area) */}
        {capPageLayout !== null && (capPageLayout[0]?.length ?? 0) > 0 && (
          <div style={{ padding: '0 40px' }}>
            {capPageLayout[0].map((section, sectionIdx) => {
              const d = areaCalcList[section.areaIndex]
              const isFirstCapSection = sectionIdx === 0
              if (section.kind === 'chart') {
                return (
                  <div key={`p1-${section.areaIndex}-chart`}>
                    {isFirstCapSection && <div className="text-2xl font-bold pb-2 text-black">Capability details</div>}
                    {progress.areas.length > 1 && (
                      <div className={`text-xl font-bold text-black mb-1${!isFirstCapSection ? ' mt-8' : ''}`}>
                        {d.areaIndex + 1}. {d.area.areaTitle || d.area.areaCode}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-4">
                      <div style={{ width: 500, marginTop: '-16px', marginBottom: '-40px' }}>
                        <Radar data={d.areaRadarData} options={areaRadarOpts} />
                      </div>
                      <div><MiniAiGauge pct={d.aiPct} id={`ai-p1-${d.area.areaCode}`} /></div>
                    </div>
                  </div>
                )
              }
              if (section.kind === 'summary') {
                return (
                  <div key={`p1-${section.areaIndex}-summary`} className="mt-4">
                    <div className="text-sm font-semibold text-black pb-1">Summary</div>
                    {d.summaryComments.length > 0
                      ? d.summaryComments.map((comment, i) => (
                          <div key={i} className="pl-3 py-0.5 flex gap-1">
                            <span className="text-sm text-black flex-shrink-0">•</span>
                            <p className="text-sm text-black font-roboto">{comment}</p>
                          </div>
                        ))
                      : <p className="text-sm text-black font-roboto">{d.genericStrengthComment}</p>
                    }
                  </div>
                )
              }
              if (section.kind === 'aod') {
                return (
                  <div key={`p1-${section.areaIndex}-aod`} className="mt-3">
                    <div className="text-sm font-semibold text-black pb-1">Areas for development</div>
                    <p className="text-sm text-black font-roboto pb-1">Here are some learning outcomes you answered incorrectly on and will help you strengthen your digital capability:</p>
                    {d.areaOutcomes.map((lo, i) => (
                      <div key={i} className="pl-3 py-0.5 flex gap-1">
                        <span className="text-sm text-black flex-shrink-0">•</span>
                        <p className="text-sm text-black font-roboto">{lo.description}</p>
                      </div>
                    ))}
                  </div>
                )
              }
              if (section.kind === 'future') {
                const isLastPage = (capPageLayout?.slice(1).length ?? 0) === 0
                const isLastSectionOnPage = sectionIdx === (capPageLayout?.[0]?.length ?? 0) - 1
                return (
                  <div key={`p1-${section.areaIndex}-future`} className="mt-3">
                    <div className="text-sm font-semibold text-black pb-1">Future goals</div>
                    <p className="text-sm text-black font-roboto pb-1">Here are some learning outcomes to focus on to develop your capabilities to the next level:</p>
                    {d.futureGoals.map((lo, i) => (
                      <div key={i} className="pl-3 py-0.5 flex gap-1">
                        <span className="text-sm text-black flex-shrink-0">•</span>
                        <p className="text-sm text-black font-roboto">{lo.description}</p>
                      </div>
                    ))}
                    {false && isLastPage && isLastSectionOnPage && (
                      <div className="mt-10 flex justify-center">
                        <a
                          className="inline-flex items-center justify-center gap-2 border-2 px-4 py-2 hover:brightness-95 active:scale-[0.99] transition-all"
                          style={{ backgroundColor: 'rgb(255, 220, 0)', color: 'rgb(51, 51, 51)', borderColor: 'rgb(51, 51, 51)', fontFamily: 'RobotoMono, Arial, sans-serif', fontSize: 16, fontWeight: 400 }}
                          href={coursesHref}
                        >
                          View recommended courses
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>
                )
              }
              return null
            })}
          </div>
        )}
        {/* Spinner while layout is being calculated (single-area; multi-area uses the spinner page below) */}
        {progress.areas.length === 1 && capPageLayout === null && (
          <div className="flex-1 flex items-center justify-center">
            <ReactLoading type={'spinningBubbles'} color={'#888888'} height={'48px'} width={'48px'} />
          </div>
        )}

        <V3Footer />
      </div>

      {/* Capability Details — dynamic layout: sections packed into pages by measured height */}
      {/* Page 0 is always rendered inside page 1 above; only render overflow pages here */}
      {capPageLayout === null && progress.areas.length > 1 ? (
        <div className="report-page flex flex-col" style={{ minHeight: '1122px' }}>
          <div style={{ padding: '32px 40px' }} className="flex-1 flex items-center justify-center">
            <ReactLoading type={'spinningBubbles'} color={'#888888'} height={'48px'} width={'48px'} />
          </div>
          <V3Footer />
        </div>
      ) : (capPageLayout ?? []).slice(1).map((pageSections, pageIdx) => (
        <div key={`cap-page-${pageIdx}`} className="report-page flex flex-col" style={{ minHeight: '1122px' }}>
          <div style={{ padding: '32px 40px' }} className="flex-1">
            {pageSections.map((section, sectionIdx) => {
              const d = areaCalcList[section.areaIndex]
              // Show "Capability details" heading on the very first cap section across all pages.
              // capPageLayout[0] may be empty (when nothing fit on page 1), in which case the
              // first overflow page (pageIdx===0) carries the heading.
              const page0Empty = (capPageLayout?.[0]?.length ?? 0) === 0
              const isFirstCapSection = page0Empty && pageIdx === 0 && sectionIdx === 0

              if (section.kind === 'chart') {
                return (
                  <div key={`${section.areaIndex}-chart`}>
                    {isFirstCapSection && (
                      <div className="text-2xl font-bold pb-2 text-black">Capability details</div>
                    )}
                    {progress.areas.length > 1 && (
                      <div className={`text-xl font-bold text-black mb-1${!isFirstCapSection ? ' mt-8' : ''}`}>
                        {d.areaIndex + 1}. {d.area.areaTitle || d.area.areaCode}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-4">
                      <div style={{ width: 500, marginTop: '-16px', marginBottom: '-40px' }}>
                        <Radar data={d.areaRadarData} options={areaRadarOpts} />
                      </div>
                      <div><MiniAiGauge pct={d.aiPct} id={`ai-${d.area.areaCode}`} /></div>
                    </div>
                  </div>
                )
              }

              if (section.kind === 'summary') {
                return (
                  <div key={`${section.areaIndex}-summary`} className="mt-4">
                    <div className="text-sm font-semibold text-black pb-1">Summary</div>
                    {d.summaryComments.length > 0
                      ? d.summaryComments.map((comment, i) => (
                          <div key={i} className="pl-3 py-0.5 flex gap-1">
                            <span className="text-sm text-black flex-shrink-0">•</span>
                            <p className="text-sm text-black font-roboto">{comment}</p>
                          </div>
                        ))
                      : <p className="text-sm text-black font-roboto">{d.genericStrengthComment}</p>
                    }
                  </div>
                )
              }

              if (section.kind === 'aod') {
                return (
                  <div key={`${section.areaIndex}-aod`} className="mt-3">
                    <div className="text-sm font-semibold text-black pb-1">Areas for development</div>
                    <p className="text-sm text-black font-roboto pb-1">Here are some learning outcomes you answered incorrectly on and will help you strengthen your digital capability:</p>
                    {d.areaOutcomes.map((lo, i) => (
                      <div key={i} className="pl-3 py-0.5 flex gap-1">
                        <span className="text-sm text-black flex-shrink-0">•</span>
                        <p className="text-sm text-black font-roboto">{lo.description}</p>
                      </div>
                    ))}
                  </div>
                )
              }

              if (section.kind === 'future') {
                const isLastOverflowPage = pageIdx === (capPageLayout ?? []).slice(1).length - 1
                const isLastSectionOnPage = sectionIdx === pageSections.length - 1
                return (
                  <div key={`${section.areaIndex}-future`} className="mt-3">
                    <div className="text-sm font-semibold text-black pb-1">Future goals</div>
                    <p className="text-sm text-black font-roboto pb-1">Here are some learning outcomes to focus on to develop your capabilities to the next level:</p>
                    {d.futureGoals.map((lo, i) => (
                      <div key={i} className="pl-3 py-0.5 flex gap-1">
                        <span className="text-sm text-black flex-shrink-0">•</span>
                        <p className="text-sm text-black font-roboto">{lo.description}</p>
                      </div>
                    ))}
                    {false && isLastOverflowPage && isLastSectionOnPage && (
                      <div className="mt-10 flex justify-center">
                        <a
                          className="inline-flex items-center justify-center gap-2 border-2 px-4 py-2 hover:brightness-95 active:scale-[0.99] transition-all"
                          style={{ backgroundColor: 'rgb(255, 220, 0)', color: 'rgb(51, 51, 51)', borderColor: 'rgb(51, 51, 51)', fontFamily: 'RobotoMono, Arial, sans-serif', fontSize: 16, fontWeight: 400 }}
                          href={coursesHref}
                        >
                          View recommended courses
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>
                )
              }

              return null
            })}
          </div>
          <V3Footer />
        </div>
      ))}

      {/* Hidden measurement container for wrongOutcomes (reserved for future use) */}
      <div ref={measureRef} className="report-measure-hidden" style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', width: 'calc(794px - 80px)' }} aria-hidden="true">
        {wrongOutcomes.map((lo, i) => (
          <div key={i} className="pl-3 py-0.5 border-l-2 flex gap-1" style={{ borderColor: lo.type === 'Knowledge' ? RC.blue : lo.type === 'Skill' ? RC.green : RC.orange }}>
            <span className="text-sm text-black flex-shrink-0">•</span>
            <p className="text-sm text-black font-roboto">{lo.description}</p>
          </div>
        ))}
      </div>

      {/* Hidden measurement container for capability sections */}
      <div ref={capMeasureRef} className="report-measure-hidden" style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', width: 'calc(794px - 80px)' }} aria-hidden="true">
        {capSections.map((section, i) => {
          const d = areaCalcList[section.areaIndex]
          if (section.kind === 'chart') {
            // Fixed-height placeholder avoids async Chart.js rendering causing wrong measurements.
            // The first chart includes the section heading and measures 436px (multi-area, with area title)
            // or 410px (single-area, no area title). Subsequent chart wrappers render at 396px in the
            // live layout; the title's mt-8 collapses outside the wrapper, so including that 32px here
            // overestimates height and pushes following summaries onto the next page prematurely.
            const isFirst = i === 0
            const h = isFirst ? (progress.areas.length === 1 ? 410 : 436) : 396
            return <div key={i} style={{ height: h }} />
          }
          if (section.kind === 'summary') {
            return (
              <div key={i} className="mt-4">
                <div className="text-sm font-semibold text-black pb-1">Summary</div>
                {d.summaryComments.length > 0
                  ? d.summaryComments.map((comment, j) => (
                      <div key={j} className="pl-3 py-0.5 flex gap-1">
                        <span className="text-sm text-black flex-shrink-0">•</span>
                        <p className="text-sm text-black font-roboto">{comment}</p>
                      </div>
                    ))
                  : <p className="text-sm text-black font-roboto">{d.genericStrengthComment}</p>
                }
              </div>
            )
          }
          if (section.kind === 'aod') {
            return (
              <div key={i} className="mt-3">
                <div className="text-sm font-semibold text-black pb-1">Areas for development</div>
                <p className="text-sm text-black font-roboto pb-1">Here are some learning outcomes you answered incorrectly on and will help you strengthen your digital capability:</p>
                {d.areaOutcomes.map((lo, j) => (
                  <div key={j} className="pl-3 py-0.5 flex gap-1">
                    <span className="text-sm text-black flex-shrink-0">•</span>
                    <p className="text-sm text-black font-roboto">{lo.description}</p>
                  </div>
                ))}
              </div>
            )
          }
          if (section.kind === 'future') {
            return (
              <div key={i} className="mt-3">
                <div className="text-sm font-semibold text-black pb-1">Future goals</div>
                <p className="text-sm text-black font-roboto pb-1">Here are some learning outcomes to focus on to develop your capabilities to the next level:</p>
                {d.futureGoals.map((lo, j) => (
                  <div key={j} className="pl-3 py-0.5 flex gap-1">
                    <span className="text-sm text-black flex-shrink-0">•</span>
                    <p className="text-sm text-black font-roboto">{lo.description}</p>
                  </div>
                ))}
              </div>
            )
          }
          return null
        })}
      </div>

    </div>
  )
}
